import { buildUrlWithParams } from 'src/lib/utils';
import { PATHS } from '../config';
import { dynoapeAPI } from '../dynoape';
import type {
    ActiveLicenseRequest,
    Department,
    DepartmentParams,
    DepartmentWithLicenseRequest,
    OrgDepartmentsResponse,
    Position,
    Trait,
    UpdateDepartmentRequest,
} from './Models';

const getDepartment = async (
    departmentId: string,
    params?: DepartmentParams,
): Promise<Department> =>
    await dynoapeAPI.get(PATHS.department(departmentId, params));

const getPositions = async (departmentId: string): Promise<Position[]> =>
    await dynoapeAPI.get(PATHS.positions(departmentId));

const getTraits = async (departmentId: string): Promise<Trait[]> =>
    await dynoapeAPI.get(PATHS.traits(departmentId));

/*
 * Departments grouped by ownership, each wrapped with its license
 * ({ department, license }) - the license is requested via the
 * includeLicense parameter.
 */
const getOrgDepartmentsWithLicense = async (
    orgId: string,
): Promise<OrgDepartmentsResponse> =>
    await dynoapeAPI.get(
        buildUrlWithParams(PATHS.orgDepartments(orgId), {
            includeLicense: true,
        }),
    );

const updateDepartment = async (
    departmentId: string,
    department: UpdateDepartmentRequest,
): Promise<Department> =>
    await dynoapeAPI.put(PATHS.department(departmentId), department);

const updateDepartmentActiveLicense = async (
    departmentId: string,
    license: ActiveLicenseRequest,
): Promise<void> =>
    await dynoapeAPI.put(PATHS.departmentActiveLicense(departmentId), license);

const createDepartmentWithLicense = async (
    orgId: string,
    department: DepartmentWithLicenseRequest,
): Promise<void> =>
    await dynoapeAPI.post(PATHS.departmentWithLicense(orgId), department);

const deleteDepartment = async (departmentId: string): Promise<void> =>
    await dynoapeAPI.delete(PATHS.department(departmentId));

const removeDepartmentAccess = async (departmentId: string): Promise<void> =>
    await dynoapeAPI.delete(PATHS.departmentUnshareMe(departmentId));

/*
 * The endpoint returns departments grouped by ownership, each wrapped
 * with its license ({ department, license }) - see the organization home feature
 * and OrgDepartmentsResponse. The license is only present when requested via
 * the includeLicense parameter.
 */
const getOrgDepartments = async (orgId: string): Promise<Department[]> => {
    const response: OrgDepartmentsResponse | undefined = await dynoapeAPI.get(
        PATHS.orgDepartments(orgId),
    );

    return [...(response?.owned ?? []), ...(response?.external ?? [])].map(
        ({ department }) => department,
    );
};

const DepartmentService = {
    getDepartment,
    getPositions,
    getTraits,
    getOrgDepartments,
    getOrgDepartmentsWithLicense,
    updateDepartment,
    updateDepartmentActiveLicense,
    createDepartmentWithLicense,
    deleteDepartment,
    removeDepartmentAccess,
};

export default DepartmentService;
